import React, { useEffect, useState } from 'react';
import { Container, Button } from 'reactstrap';
import { IntlForm } from 'react-ocean-forms-react-intl';
import { ViewMode } from '../../../components/Common/ViewMode';
import { ParameterNewEditForm } from './ParameterNewEditForm';
import { useLazyQuery } from '@apollo/client';
import { ParameterGraphQLService } from '../../../graphql/ParameterGraphQLService';
import { ParameterService, ParameterStatusUtil } from '../../../api';
import { BackButton } from '../../../components/Navigation';
import { FormattedMessage } from 'react-intl';
import { FormButton } from 'react-ocean-forms-bootstrap';
import { CreateWorkflow } from '../../../components/Common/CreateWorkflow';
import { readView, editView, newView, deleteView } from './viewConfig';
import { ViewConfig } from '../../../components';
import { PCCard } from '../../../components/Common';
import { PermissionComponent } from '../../../components/Common/PermissionComponent';
import { Permissions } from '../../../Permissions';
import { generatePath, Link, useLocation } from 'react-router-dom';
import { Routes } from '../../../Routes';
import qs from 'qs';
import { ParameterFormValues, useParameterContext } from '../../../components/Parameter';
import { DimensionQuery } from '../../../graphql/Components';
import { useUser } from '@oceanjs/core';
import { WisaUser } from '../../../components/WisaUserMgmt';
import { useRole } from '../../../hooks';
import { Role } from '../../../Roles';
import { Einheit, ParameterStatus } from '../../../generated/types';

interface ParameterNewEditProps {
  viewMode: ViewMode;
  parameterId?: string;
  onSubmit(values: any, args: any): void;
  onReset(): void;
  workflow: CreateWorkflow;
  setWorkflow(workflow: CreateWorkflow): void;
}

/**
 * component for NEW/READ/EDIT view of Parameters
 * @author Jungwirth
 */
export const ParameterNewEdit: React.FC<ParameterNewEditProps> = (props: Readonly<ParameterNewEditProps>): JSX.Element | null => {
  const { viewMode, onSubmit, parameterId, onReset = () => {}, workflow, setWorkflow } = props;
  const { getFilter } = useParameterContext();
  const [ defaultValues, setDefaultValues ] = useState<ParameterFormValues | {}>({});
  const [ status, setStatus ] = useState<any|undefined>(undefined);
  const location = useLocation();
  const role = useRole();
  const user = useUser<WisaUser>();

  const [ loadParameter, { loading } ] = useLazyQuery(
    ParameterGraphQLService.getParameterDetails(),
    { fetchPolicy: 'network-only',
      variables: { id: parameterId }}
  );

  
  const renderEditModeButtons = (): JSX.Element | null => {
    return (
      <React.Fragment>
        <div className="float-right">
          <BackButton color="link" type="button">
            <FormattedMessage id="button_cancel" />
          </BackButton>{' '}
          <FormButton
            color="primary"
            submitArgs="duplicate"
            disabled={workflow !== CreateWorkflow.CheckDuplicates}
            onClick={(values: any) => {
              console.log('duplicate check');
              setWorkflow(CreateWorkflow.AllowSave);
            }
            }>
            <FormattedMessage id="button_duplicate_check" />
          </FormButton>{' '}
          <FormButton
            color="primary"
            submitArgs="newedit"
            disabled={workflow !== CreateWorkflow.AllowSave}
            type="submit">
            {viewMode === ViewMode.New &&
              <FormattedMessage id="button_save_return" />
            }
            {viewMode === ViewMode.Edit &&
              <FormattedMessage id="button_edit_return" />
            }
          </FormButton>{' '}
          <Button
            color="primary"
            type="reset"
            onClick={() => {
              onReset();
              setWorkflow(CreateWorkflow.CheckDuplicates);
            }}
          >
            <FormattedMessage id="button_reset" />
          </Button>{' '}
        </div>
      </React.Fragment>
    );

  }

  const renderReadModeButtons = (): JSX.Element | null => {
    return (
      <React.Fragment>
        <div className="float-right">
          <BackButton color="link" type="button">
            <FormattedMessage id="button_return" />
          </BackButton>{' '}
          <PermissionComponent permission={[Permissions.ParameterUpdate]}>
            <Button
              color="primary"
              type="button"
              disabled={!(role === Role.EHPC_RW && !(status === ParameterStatus.Ps3))}
              tag={Link} 
              to={generatePath(Routes.ParameterEdit, {parameterId: parameterId })}
            >
              <FormattedMessage id="button_edit" />
            </Button>
          </PermissionComponent>
        </div>
      </React.Fragment>
    )
  }

  const renderDeleteModeButtons = (): JSX.Element => {
    return (
      <React.Fragment>
        <div className="float-right">
          <BackButton color="link" type="button">
            <FormattedMessage id="button_return" />
          </BackButton>
          <PermissionComponent permission={[Permissions.ParameterKomponenteDelete]}>
            <FormButton
              color="primary"
              type="submit"
              submitArgs="deny"
            >
              <FormattedMessage id="button_delete" />
            </FormButton>
          </PermissionComponent>
        </div>
      </React.Fragment>
    );
  }

  // data loading for READ/EDIT view
  useEffect(() => {
    const loadOnInit = async () => {
      const result = await loadParameter();
      
      if(result.data) {
        const formData = ParameterService.transformDataToForm(result.data['einParameter']);
        formData['anforderung'].email =  formData['anforderung'].email === 'mustermann@musterstadt.at' ? '' : formData['anforderung'].email;
        setStatus(result.data[Object.keys(result.data)[0]]?.status);
        setDefaultValues(formData);
      }
    }
    if(parameterId) {
      loadOnInit();
    }

  },[parameterId, loadParameter]);
  
  // effect for new with search params
  useEffect(() => {
    const searchparams = location.search;

    const params = qs.parse(searchparams, { ignoreQueryPrefix: true });
    if(params && params.hasOwnProperty('searchparams') && getFilter() !== undefined) {
      const formData = getFilter();
      // console.log('parameter search params', formData);
      if(formData) {
        formData.unit = ParameterStatusUtil.removeInactiveStatus(formData.unit);
        formData.material = ParameterStatusUtil.removeInactiveStatus(formData.material);
        formData.non_material = ParameterStatusUtil.removeInactiveStatus(formData.non_material);
        formData.sample_pretreatment = ParameterStatusUtil.removeInactiveStatus(formData.sample_pretreatment);
        formData.method = ParameterStatusUtil.removeInactiveStatusList(formData.method);
        formData.other_criterion = ParameterStatusUtil.removeInactiveStatusList(formData.other_criterion);
        formData.matrix = ParameterStatusUtil.removeInactiveStatusList(formData.matrix);

        if (formData.unit) {
          const einheit: Einheit = JSON.parse(formData.unit.value);
          formData.dimension = DimensionQuery.getSelectOption(einheit.dimension ?? null);
        } else {
          formData.dimension = undefined;
        }
      
        formData['code'] = '';
        // @ts-ignore
        formData['status'] = undefined;
        setDefaultValues(formData);
      }
    } else if(viewMode === ViewMode.New) {
      setDefaultValues({})
    }

  },[getFilter, location.search, viewMode]);

  
  const predefinedValues = {
    status: ParameterStatusUtil.getStatusOption(ParameterStatusUtil.PS1),
    anforderung: {
      email: viewMode === ViewMode.New ? user?.email ?? '' : '',
    }
  }; 
  
  let viewConfig: ViewConfig = readView;
  let renderButtons;
  switch(viewMode) {
    case ViewMode.Read:
      viewConfig = readView;
      renderButtons = renderReadModeButtons;
      break;
    case ViewMode.Edit:
      viewConfig = editView;
      renderButtons = renderEditModeButtons;
      break;
    case ViewMode.New:
      viewConfig = newView;
      renderButtons = renderEditModeButtons;
      break;
    case ViewMode.Delete:
      viewConfig = deleteView;
      renderButtons = renderDeleteModeButtons;
      break;
  }

  return (
    <Container className="p-4" fluid>
      <IntlForm
        defaultValues={{...predefinedValues, ...defaultValues}}
        onSubmit={onSubmit}>
          <div style={{ hyphens: 'auto'}}>
            <PCCard
              id="attributes"
              loading={loading}
              footer={renderButtons}>
              <ParameterNewEditForm
                viewConfig={viewConfig}
                updateWorkflow={setWorkflow}
                />
            </PCCard>
          </div>
      </IntlForm>
    </Container>
  );
};
